/**
 * customerApi.js — B2B Cart, Checkout, Quote API helpers
 *
 * Cart-mutation helpers (add/remove/update/clear/addLines) emit a global
 * `cop31:cart-changed` DOM event after success. CustomerDashboard listens
 * for this event and silently re-syncs its cart state, so badges, totals
 * and the drawer stay accurate no matter which component triggered the
 * mutation. No visible loading flicker, no manual refresh.
 */
import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/customer`;

const auth = () => ({
  headers: {
    Authorization: `Bearer ${
      localStorage.getItem('cop31_token') ||
      localStorage.getItem('auth_token') ||
      localStorage.getItem('token') || ''
    }`
  }
});

// Tiny helper — fires after a successful cart mutation. Wrapped in try/catch
// so an unsupported environment (SSR, tests) never breaks the API call.
const emitCartChanged = () => {
  try { window.dispatchEvent(new CustomEvent('cop31:cart-changed')); }
  catch { /* no-op */ }
};

const tap = (fn) => async (...args) => {
  const r = await fn(...args);
  emitCartChanged();
  return r;
};

// ── Cart ──────────────────────────────────────────────────────────────────────
export const getCart       = () => axios.get(`${BASE}/cart`, auth()).then(r => r.data);
export const addToCart     = tap((item) => axios.post(`${BASE}/cart/add`, item, auth()).then(r => r.data));
export const addLines      = tap((lines) => axios.post(`${BASE}/cart/add-lines`, { lines }, auth()).then(r => r.data));
export const updateCartItem = tap((itemId, quantity) =>
  axios.put(`${BASE}/cart/items/${itemId}`, { quantity }, auth()).then(r => r.data));
export const removeFromCart = tap((itemId) =>
  axios.delete(`${BASE}/cart/items/${itemId}`, auth()).then(r => r.data));
export const clearCart     = tap(() => axios.delete(`${BASE}/cart/clear`, auth()).then(r => r.data));

// ── Supplier Products (Customer-facing) ───────────────────────────────────────
export const listSupplierProducts   = (params) =>
  axios.get(`${BASE}/supplier-products`, { ...auth(), params }).then(r => r.data);
export const getSupplierProduct     = (id, qty, pricingType) =>
  axios.get(`${BASE}/supplier-products/${id}`, { ...auth(), params: { quantity: qty, pricing_type: pricingType } }).then(r => r.data);
export const checkSupplierAvailability = (id, qty, pricingType) =>
  axios.post(`${BASE}/supplier-products/${id}/check-availability`, { quantity: qty, pricing_type: pricingType }, auth()).then(r => r.data);
export const getOrders     = () => axios.get(`${BASE}/orders`, auth()).then(r => r.data);
export const checkout      = (req) => axios.post(`${BASE}/checkout`, req, auth()).then(r => r.data);
export const requestQuote  = (req) => axios.post(`${BASE}/request-quote`, req, auth()).then(r => r.data);
export const acceptQuote   = (orderId) => axios.post(`${BASE}/orders/${orderId}/quote/accept`, {}, auth()).then(r => r.data);
export const rejectQuote   = (orderId) => axios.post(`${BASE}/orders/${orderId}/quote/reject`, {}, auth()).then(r => r.data);
